/**
 * UserMessage - Renders a user message bubble with text and file attachments
 *
 * Supports inline editing with "Save & Regenerate" functionality.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { FileText, Image, Copy, Check, Pen, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import type { UIMessage, FilePart } from '../../types'
import { getMessageText, formatMessageTime } from '../../utils/messageConverter'
import { highlightAgentMentions } from '../../utils/highlightAgentMentions'

interface UserMessageProps {
  message: UIMessage
  onImageClick?: (imageUrl: string) => void
  // Edit mode props
  isEditing?: boolean
  isEditSaving?: boolean
  messagesAfterCount?: number
  onEditClick?: () => void
  onEditConfirm?: (newContent: string) => void
  onEditCancel?: () => void
  canEdit?: boolean
}

export function UserMessage({
  message,
  onImageClick,
  isEditing = false,
  isEditSaving = false,
  messagesAfterCount = 0,
  onEditClick,
  onEditConfirm,
  onEditCancel,
  canEdit = true,
}: UserMessageProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const text = getMessageText(message)

  // Reset edited content when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditedContent(text)
      // Focus textarea after a short delay to ensure it's rendered
      setTimeout(() => {
        textareaRef.current?.focus()
        textareaRef.current?.select()
      }, 50)
    }
  }, [isEditing, text])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success(t('chatv2:toasts.message_copied', { defaultValue: 'Message copied to clipboard' }))
    } catch {
      toast.error(t('chatv2:toasts.copy_failed', { defaultValue: 'Failed to copy to clipboard' }))
    }
  }, [text])

  const handleEditSubmit = useCallback(() => {
    const trimmed = editedContent.trim()
    if (trimmed && !isEditSaving) {
      onEditConfirm?.(trimmed)
    }
  }, [editedContent, isEditSaving, onEditConfirm])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Enter to submit (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEditSubmit()
    }
    // Escape to cancel
    if (e.key === 'Escape') {
      e.preventDefault()
      onEditCancel?.()
    }
  }, [handleEditSubmit, onEditCancel])

  // Extract file parts from message
  const fileParts = message.parts.filter((p): p is FilePart => p.type === 'file')

  // Check if text contains "[Attached files: ...]" and extract clean text
  const attachedFilesMatch = text.match(/\n?\n?\[Attached files: .+\]$/)
  const cleanText = attachedFilesMatch
    ? text.replace(attachedFilesMatch[0], '').trim()
    : text

  // Edit mode render
  if (isEditing) {
    return (
      <div className="flex flex-col items-end w-full">
        <div className="max-w-[80%] w-full">
          {/* Warning when editing will delete subsequent messages */}
          {messagesAfterCount > 0 && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm bg-amber-50 dark:bg-amber-900/20 p-3 rounded-t-lg border border-amber-200 dark:border-amber-800 border-b-0">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {t('chatv2:messages.edit_remove_warning', { count: messagesAfterCount, defaultValue: 'This will remove {{count}} messages after this point.' })}
              </span>
            </div>
          )}

          {/* Edit textarea */}
          <div
            className={clsx(
              'bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text p-3',
              messagesAfterCount > 0 ? 'rounded-b-2xl' : 'rounded-2xl'
            )}
          >
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={handleKeyDown}
              className={clsx(
                'w-full bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text rounded-lg p-3 text-sm',
                'border border-gray-300 dark:border-gray-600 focus:border-gray-400 focus:ring-1 focus:ring-gray-400',
                'resize-none outline-none min-h-[80px]',
                isEditSaving && 'opacity-50'
              )}
              disabled={isEditSaving}
              placeholder={t('chatv2:messages.edit_placeholder', { defaultValue: 'Edit your message...' })}
            />

            {/* Action buttons */}
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={onEditCancel}
                disabled={isEditSaving}
                className={clsx(
                  'px-3 py-1.5 text-sm rounded-lg',
                  'bg-gray-200 dark:bg-dark-border text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                {t('chatv2:feedback.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={!editedContent.trim() || isEditSaving}
                className={clsx(
                  'px-3 py-1.5 text-sm rounded-lg font-medium',
                  'bg-orange-500 text-white hover:bg-orange-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                {isEditSaving
                  ? t('chatv2:messages.edit_saving', { defaultValue: 'Saving...' })
                  : messagesAfterCount > 0
                    ? t('chatv2:messages.edit_continue', { defaultValue: 'Edit & Continue' })
                    : t('chatv2:messages.edit_save_regenerate', { defaultValue: 'Save & Regenerate' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Normal display mode
  return (
    <div className="flex flex-col items-end group">
      <div className="flex items-center gap-2">
        {/* Edit button - shown on hover */}
        {canEdit && onEditClick && (
          <button
            onClick={onEditClick}
            className={clsx(
              'p-2 rounded-full border border-gray-300 dark:border-gray-600',
              'opacity-0 group-hover:opacity-60 hover:!opacity-100',
              'hover:bg-gray-100 dark:hover:bg-dark-accent transition-all'
            )}
            title={t('chatv2:messages.edit', { defaultValue: 'Edit message' })}
          >
            <Pen className="h-4 w-4 text-gray-500 dark:text-dark-text-muted" />
          </button>
        )}

        {/* Message bubble - light gray to match Figma */}
        <div
          className={clsx(
            'max-w-[80%] rounded-2xl overflow-hidden',
            'bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text'
          )}
        >
          {/* File attachments preview */}
          {fileParts.length > 0 && (
            <div className="p-2 bg-gray-200 dark:bg-dark-border border-b border-gray-300 dark:border-gray-600">
              <div className="flex flex-wrap gap-2">
                {fileParts.map((file, index) => {
                  const isImage = file.mediaType?.startsWith('image/')

                  if (isImage && (file.data || file.url)) {
                    const imgSrc = file.data
                      ? `data:${file.mediaType};base64,${file.data}`
                      : file.url
                    return (
                      <button
                        key={index}
                        onClick={() => onImageClick?.(imgSrc)}
                        className="relative group"
                      >
                        <img
                          src={imgSrc}
                          alt={file.title || t('chatv2:messages.attached_image', { defaultValue: 'Attached image' })}
                          className="h-16 w-16 object-cover rounded-lg hover:opacity-80 transition-opacity"
                        />
                      </button>
                    )
                  }

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-dark-card rounded-lg border border-gray-300 dark:border-gray-600"
                    >
                      <FileText className="h-4 w-4 text-gray-500 dark:text-dark-text-muted" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[150px] truncate">
                        {file.title || t('chatv2:messages.file', { defaultValue: 'File' })}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Text content */}
          {cleanText && (
            <div className="px-4 py-3 text-sm leading-relaxed">
              <p className="whitespace-pre-wrap">{highlightAgentMentions(cleanText)}</p>
            </div>
          )}

          {/* Show attachment indicator if files were mentioned but no parts */}
          {attachedFilesMatch && fileParts.length === 0 && (
            <div className="px-4 pb-2 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <Image className="h-3 w-3" />
              {t('chatv2:messages.files_attached', { defaultValue: 'Files attached' })}
            </div>
          )}
        </div>
      </div>

      {/* Actions row: Copy + Timestamp */}
      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors opacity-0 group-hover:opacity-100"
          title={t('chatv2:messages.copy_message', { defaultValue: 'Copy message' })}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
          )}
        </button>
        {message.createdAt && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {formatMessageTime(message.createdAt)}
          </span>
        )}
      </div>
    </div>
  )
}

export default UserMessage
